import { Util } from '@antv/g6';
import { Position } from '../../../constants';
import { ChildNodeModel, DrawOpts, Group, NodeModel } from '../../../types';
import { groupIds, shapeNames } from '../../constants';
import {
  adjustAttrsPosition,
  drawText,
  getOffsetsSum,
  getPositionRelativeToShape,
} from '../../utils';
import { baseUtils, NODE_SECTIONS } from '../base';
import { DrawFooterDetailsOptions, GetFooterGroupOpts } from './baseWithFooter.types';

const getFooterData = (model: NodeModel | ChildNodeModel) =>
  baseUtils.getTextData(NODE_SECTIONS.DESC_FOOTER, model as NodeModel);

export const hasFooterText = (model: NodeModel | ChildNodeModel) => {
  const { text, segmentConfig } = getFooterData(model);

  return !!text || !!segmentConfig?.segments?.length;
};

export const getDefaultYStart = (options: DrawOpts['options']) => options?.footerStartY ?? 0;

export const getDividerDimensions = ({ options, size }: Omit<DrawOpts, 'group' | 'model'>) => {
  const [width] = size;
  const { isFullWidth = false, padding: footerPadding } = options[shapeNames.divider];
  const [, rightOffset, , leftOffset] = getOffsetsSum(
    footerPadding,
    isFullWidth ? 0 : options.padding,
  );

  return {
    width: width - leftOffset - rightOffset,
    x: leftOffset,
    y: getDefaultYStart(options),
  };
};

export const getFooterShapeAttrs = ({ options, size }: Omit<DrawOpts, 'group' | 'model'>) => ({
  background: {
    ...options[shapeNames.divider],
    ...getDividerDimensions({ options, size }),
  },
});

const getFooterWidth = ({ options, size }: Pick<DrawOpts, 'options' | 'size'>) => {
  const [width = 0] = size || [];
  const [, paddingRight, , paddingLeft] = Util.formatPadding(options.padding);
  return width - paddingRight - paddingLeft;
};

const getDescFooterDrawArgs = ({
  footerGroup,
  group,
  model,
  options,
  size,
  yRelativeTo = shapeNames.divider,
  x = 0,
}: DrawFooterDetailsOptions) => {
  const style = options[shapeNames.footerText];
  const { text = '', segmentConfig } = getFooterData(model as NodeModel);
  const maxWidth = getFooterWidth({ options, size });
  const startY = getPositionRelativeToShape({
    relativeTo: yRelativeTo,
    style,
    defaultPosition: getDefaultYStart(options),
    group,
    position: Position.Bottom,
  });

  return {
    className: shapeNames.footerText,
    group: footerGroup,
    maxWidth,
    segmentConfig,
    startX: (style.x ?? Util.formatPadding(options.padding)[3]) + x,
    startY,
    text,
    style,
  };
};

export const drawFooterText = (opts: DrawFooterDetailsOptions) => {
  return drawText(getDescFooterDrawArgs(opts));
};

export const getFooterGroup = ({
  group,
  groupId = groupIds.footer,
  isVisible = false,
}: GetFooterGroupOpts) =>
  (group.findById(groupId) as Group) ||
  group.addGroup({ id: groupId, visible: isVisible, zIndex: 6 });

interface GetDividerOpts extends GetFooterGroupOpts, Omit<DrawOpts, 'model'> {
  dividerShapeName?: string;
  getFooterGroup?: typeof getFooterGroup;
}

export const getDivider = (opts: GetDividerOpts) => {
  const {
    dividerShapeName = shapeNames.divider,
    getFooterGroup: getFooterGroupFn = getFooterGroup,
  } = opts;
  const footerGroup = getFooterGroupFn(opts);

  return (
    footerGroup.findByClassName(dividerShapeName) ||
    footerGroup.addShape('rect', {
      name: dividerShapeName,
      className: dividerShapeName,
      draggable: true,
      attrs: adjustAttrsPosition(getFooterShapeAttrs(opts).background, opts),
    })
  );
};

interface GetFooterContentGroupOpts extends GetFooterGroupOpts {
  getFooterGroup?: (opts: GetFooterGroupOpts) => Group;
}

export const getFooterContentGroup = (opts: GetFooterContentGroupOpts) => {
  const { getFooterGroup: getFooterGroupFn = getFooterGroup } = opts;
  const footerGroup = getFooterGroupFn(opts);
  const footerContentGroup = footerGroup.findById(groupIds.footerContent) as Group;
  return footerContentGroup || footerGroup.addGroup({ id: groupIds.footerContent });
};

interface DrawFooterOpts
  extends DrawFooterDetailsOptions,
    DrawOpts,
    GetFooterGroupOpts,
    GetDividerOpts {
  drawFooterContent?: typeof drawFooterText;
  getDivider?: typeof getDivider;
  getFooterContentGroup?: typeof getFooterContentGroup;
  hasFooter?: typeof hasFooterText;
}

export const drawFooter = (opts: DrawFooterOpts) => {
  const {
    model,
    hasFooter = hasFooterText,
    getFooterGroup: getFooterGroupFn = getFooterGroup,
    getDivider: getDividerFn = getDivider,
    getFooterContentGroup: getFooterContentGroupFn = getFooterContentGroup,
    drawFooterContent = drawFooterText,
    dividerShapeName = shapeNames.divider,
  } = opts;
  const isVisible = hasFooter(model as NodeModel);
  const footerGroup = getFooterGroupFn({ ...opts, isVisible });

  if (isVisible) {
    getDividerFn(opts);
    drawFooterContent({
      ...opts,
      footerGroup: getFooterContentGroupFn(opts),
      yRelativeTo: opts.yRelativeTo ?? dividerShapeName,
    });
  }

  return footerGroup;
};

export const calculateNodeWithFooterHeight = ({
  startY = 0,
  footer,
  options,
}: Pick<DrawOpts, 'options'> & { footer: Group; startY?: number }) => {
  const [, , wrapperBottom] = Util.formatPadding(options.padding);
  const [, , footerBottom] = Util.formatPadding(options[shapeNames.footerContentWrapper].padding);
  return footer.getBBox().maxY - startY + wrapperBottom + footerBottom;
};
